import React, { useState } from "react";
import Popup from "reactjs-popup";
import V3PerformerPicker from "../../../OfferEditUI/components/V3PerformerPicker";
import { addPerformer, updatePerformer, removePerformer } from "./hold/performerApi";
import { ARTIST_PICKER_MODAL_STYLE } from "../../../shared/components/artistPickerModalStyle";
import { toastError, toastSuccess } from "../../../../shared/toastHelper";

// Shared artist-picker modal for a Confirm's lineup, lifted here so both the
// Lineup card and the "Get this event ready" checklist open the same instance.
// Returns triggers (openAdd/openEdit/remove) plus the modal element to render
// once in the owning component. `enabled` is false without the team/confirm/
// refresh wiring, in which case there are no controls and no modal.
export default function useArtistPicker({ teamId, confirmId, onLineupChange }) {
  const enabled = Boolean(teamId && confirmId && onLineupChange);
  const [target, setTarget] = useState(null); // null | { mode: "add" } | { mode: "edit", performer }
  const [busyId, setBusyId] = useState(null);
  const editing = target && target.mode === "edit" ? target.performer : null;

  const openAdd = () => setTarget({ mode: "add" });
  const openEdit = (performer) => setTarget({ mode: "edit", performer });
  const close = () => setTarget(null);

  // Adapts V3PerformerPicker's { artistId, newArtistName, actType, spotifyId,
  // externalUrl } selection to the performers endpoint: add a new performer, or
  // swap the artist on the one being edited. Both link an existing artist or
  // create one inline.
  const handlePick = async ({ artistId, newArtistName, actType, spotifyId, externalUrl }, popupClose) => {
    const label = newArtistName || "Artist";
    try {
      if (editing) {
        await updatePerformer({ teamId, performerId: editing.id, artistId, newArtistName, spotifyId, externalUrl });
      } else {
        await addPerformer({ teamId, confirmId, actType: actType || "support", artistId, newArtistName, spotifyId, externalUrl });
      }
      popupClose();
      onLineupChange();
      toastSuccess(editing ? "Lineup updated" : `${label} added to your CRM`);
    } catch (e) {
      toastError("Couldn't save the artist. Please try again.");
      throw e;
    }
  };

  const remove = (id) => {
    if (busyId) return;
    setBusyId(id);
    removePerformer({ teamId, performerId: id })
      .then(() => onLineupChange())
      .catch(() => toastError("Couldn't remove the artist. Please try again."))
      .finally(() => setBusyId(null));
  };

  // One-tap add without opening the picker (the Hold card's suggested artist).
  const quickAdd = ({ artistId, newArtistName, actType }) => {
    if (busyId) return;
    setBusyId("quickAdd");
    addPerformer({ teamId, confirmId, actType: actType || "headline", artistId, newArtistName })
      .then(() => onLineupChange())
      .catch(() => toastError("Couldn't add the artist. Please try again."))
      .finally(() => setBusyId(null));
  };

  const modal = enabled ? (
    <Popup
      open={Boolean(target)}
      onClose={close}
      modal
      closeOnDocumentClick
      contentStyle={ARTIST_PICKER_MODAL_STYLE}
      className="offer-modal"
    >
      {(popupClose) => (
        <V3PerformerPicker
          mode={editing ? "edit" : "add-sibling"}
          variant="modal"
          teamId={teamId}
          defaultActType="headline"
          currentPerformer={editing ? { name: editing.name, imageUrl: editing.imageUrl } : undefined}
          description={editing
            ? "Swap in a different artist, or add a new one inline."
            : "Search your roster for an artist, or add a new one inline."}
          onSubmit={(selection) => handlePick(selection, popupClose)}
          onClose={popupClose}
        />
      )}
    </Popup>
  ) : null;

  return { enabled, openAdd, openEdit, remove, quickAdd, busyId, modal };
}
